// 190 §C / §D — THE BENTO BOARD: columns from a template, cards where a
// placement (or nothing) puts them, a drop target per column.
//
// THE DRAG SURFACE 189 §C BUILT, extracted from CaseBoardView.tsx (its
// column/drop/highlight markup, byte for byte in shape) and made generic over
// the ITEM: the board knows an item's id, its column and its card, and nothing
// else. The three Bentos mount it — the user Bento and the case Bento over
// TASKS (the Eisenhower card, src/components/EisenTaskCard.tsx), the list
// Bento over CASES (CaseBoardView, the case card). A Bento is a VIEW, never a
// record (190 (3)): a drop calls `onMove` with an item id and a column code
// and NOTHING ELSE — the host writes the placement row.
//
// THE DROP PROTOCOL IS THE EISENHOWER'S (EisenhowerDialog's quadrant): the
// dragged card sets `text/plain` to its id in its own onDragStart and the
// column reads it on drop — so the Eisenhower card drags onto this board
// unchanged, and the case card sets the same datum. The column highlights
// while a drag is over it, as 189's board did.
import React, { useLayoutEffect, useRef, useState } from "react";
import type { Dispatch, ReactNode } from "react";
import { useTranslation } from "../i18n";
import MenuSelect from "./MenuSelect";
import { bentoColumnLabel, normalizeBentoTemplateMeta } from "../lib/crmDefinitions.mjs"; // 190 round 6 [S] — the picker reads the ALL flag; 196 §A — a column's label in the app's language
import type { TFn } from "../lib/taxonomyTypes";
import { FS, RAD, Z, BENTO_COL_MIN, BENTO_COL_PAD } from "../lib/tokens"; // 206 §5 — the column figures, named
import { HScroll } from "./SharedUI"; // 190 round 9 [AQ] — the strip's own scroll affordance, the fourth use

/* global window */ // 201 §B.3 — the picker toasts its refusal through the App's window bridge (`__lxToast`), as useBento reads `localStorage` under the same directive

/** 196 §A — a column is named in the three languages the model's name is (`label` = en); the
 *  two others are optional, and a column saved before 196 holds `label` alone. Read through
 *  `bentoColumnLabel`, never `.label` directly. */
export type BentoColumnDef = Readonly<{ code: string; label: string; labelFr?: string; labelNl?: string }>;
/** 190 round 6 [S] — a card's identity on the board: a number (a task, a case of one kind) or a
 *  string the host composes when two kinds share a board (`ca:12` / `cc:12` — the two lists'
 *  ids are independent autoincrements and collide). The host decodes it on the move. */
export type BentoCardId = number | string;
export type BentoMove = Readonly<{ itemId: BentoCardId; column: string }>;
/** One card as the host renders it, with the column the host's ladder resolved
 *  (crmDefinitions.mjs bentoColumnOf) — a code the template lacks reads as the
 *  first column here. Pre-rendered, so the board carries no callback prop but
 *  `onMove` (an imported `Dispatch` — 183's rule: a named parameter in a
 *  function type is counted by no-unused-vars, and this file carries none). */
export type BentoCard = Readonly<{ id: BentoCardId; column: string; node: ReactNode }>;

/** 190 [I] (ruled 2026-09-15, correcting round 2's (i)(e), which was the
 *  advisor's) — THE PICKER: the template's name and the caret, NO LABEL, and
 *  it sits in the SAME ROW as the Bento icon, immediately right of it, on all
 *  three surfaces. So it is the HOST's to render (the host owns the icon —
 *  App's header, the case Tasks header, EntityList's filter row) from the
 *  `useBento` state the host owns; the boards take that state as a prop and
 *  render no picker of their own. One component, here beside the board it
 *  drives, so the three read as one control: the picker's width is the
 *  widest template name (MenuSelect `intrinsicWidth`, the native select's
 *  rule — 190 glance (i)(e)'s one surviving finding). Renders NOTHING while
 *  no template offers the surface — the pane body says so in words. */
export function BentoPicker({ bento, id, zIndex }: Readonly<{ bento: BentoPickerState; id: string; zIndex?: number }>) {
  const { t } = useTranslation() as { t: TFn };
  if (!bento.templates.length) return null;
  /* 190 round 6 [S](iii) — a template that spans both kinds says so in the
     picker: its name, then the word — a KEY, en "ALL" · fr "TOUS" · nl
     "ALLE", caps in all three (Laurent's spelling; the addendum: translated,
     not a literal). */
  const all = t("bento.all_suffix", { fallback: "ALL" });
  const labelOf = (tp: BentoPickerState["templates"][number]): string => {
    const name = tp.label ?? tp.code;
    return normalizeBentoTemplateMeta(tp.meta).allMatters ? `${name} - ${all}` : name;
  };
  return (
    <MenuSelect
      id={id}
      className="field-input bentoPicker"
      intrinsicWidth
      style={{ width: "auto" }}
      zIndex={zIndex}
      value={bento.templateCode ?? ""}
      /* 201 §B.3 [JUDGEMENT — advisor; built] — while the firm's default is
         imposed the picker still lists every template, and a pick of another
         one is REFUSED WITH THE TOGGLE'S OWN SENTENCE (the editor's hover, one
         key, so the two cannot drift) rather than silently ignored — nobody
         thinks the app is broken. The hook writes nothing while imposed; the
         reader's stored pick stays for the day the flag comes off. */
      onChange={(v) => {
        if (!v) return;
        if (bento.imposed && v !== bento.templateCode) {
          window.__lxToast?.(t("taxonomies.bento.impose_title", { fallback: "While this is on, everyone sees this template on that place; personal choices are ignored, not erased, and turning it off gives everyone their own back." }), "warning");
          return;
        }
        bento.setTemplate(v);
      }}
      options={bento.templates.map((tp) => [tp.code, labelOf(tp)] as const)}
    />
  );
}
/* 190 round 9 [AS] [RULED, Laurent 2026-09-17 — "on small screens when opening
   CV/CCV task bentos, the filters jump around: make the bento choice dropdown
   appear in the row below, with fixed position flush with the bento icon
   (while still allowing some filters to spill over in the beginning of that
   same row if the window is really narrow)"]. THIS DOES NOT REVERSE ROUND 4's
   [I]: beside the icon stays the rule where the row has room; below it,
   LEFT-ALIGNED WITH THE ICON, where it does not. THE ANCHOR, for the three
   hosts (the case Tasks header, the two lists' toolbars): the icon is its
   child, the picker its prop; it MEASURES whether the row has room — every
   in-flow child's width (the picker's own included, wherever it is rendered)
   plus the gaps, as ONE line, against the row's available width, in layout
   px (`offsetWidth`, never a client rect — 142's finding), the same
   arithmetic SharedUI's `useFiltersWrap` uses — and reads intrinsic widths
   only, so the answer does not depend on which mode is rendered and cannot
   oscillate (a first cut compared items by `offsetTop` to find the icon's
   line; centred items of different heights share a line and different
   offsetTops, the count was short, "beside" was answered for a picker the
   browser had wrapped, and the two renders chased each other into React's
   #185 — measured). Room → the picker is the icon's in-flow
   sibling (round 4's row). No room → it is ABSOLUTE under the icon, flush
   with its left edge, out of the row's flow (so nothing else reflows when
   the Bento opens), and the anchor reserves the picker's height under the
   icon's line (a margin, the icon pinned to the line's top) so the row below
   is real and the picker never overlays the content. Header controls may
   wrap into that second row before the picker; the picker's own position is
   what does not move. */
const ANCHOR_GAP = 4;
export function BentoPickerAnchor({ picker, gap = 5, children }: Readonly<{ picker: ReactNode; gap?: number; children: ReactNode }>) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const pickerRef = useRef<HTMLSpanElement | null>(null);
  const [below, setBelow] = useState(false);
  const [pickerH, setPickerH] = useState(0);
  useLayoutEffect(() => {
    const anchor = ref.current;
    const row = anchor ? anchor.parentElement : null;
    if (!anchor || !row) return undefined;
    const measure = () => {
      const pk = pickerRef.current;
      const pw = pk ? pk.offsetWidth : 0;
      const ph = pk ? pk.offsetHeight : 0;
      // the row's in-flow children as one line (the picker's own span excluded — its width is added once, below)
      let used = 0; let items = 0;
      for (const el of Array.from(row.children) as HTMLElement[]) {
        if (el === pk || el.dataset.bentoPickerBeside !== undefined) continue;
        // (the absolute picker is the anchor's child, never the row's — nothing else to skip)
        used += el.offsetWidth + (parseFloat(el.style.marginLeft) || 0) + (parseFloat(el.style.marginRight) || 0);
        items += 1;
      }
      used += gap * Math.max(0, items - 1);
      /* THE ROOM IS THE AVAILABLE WIDTH, NOT THE ROW'S CURRENT ONE. A list
         toolbar is a full-width flex row: its clientWidth is the room. The
         case Tasks header's icon sits inside SectionHeader's filters wrapper
         (`data-hdr-filters`, SharedUI), a shrink-wrapped flex item of the
         header row whose clientWidth is its CONTENT'S width — and whose place
         in the row is DECIDED by `useFiltersWrap` (188 §F.2): the moment the
         filters plus the picker overflow the line they share with the title,
         the wrapper takes the whole next line. So under that seam the room is
         the HEADER ROW'S width, full stop — never the wrapper's rendered
         `flexBasis`, which the parent's own layout effect writes AFTER this
         child's has read it (a first cut read it and the two effects chased
         each other: room 562 ↔ 667, React #185 — measured). */
      let room = row.clientWidth;
      if (row.dataset.hdrFilters !== undefined && row.parentElement) room = row.parentElement.clientWidth;
      const fits = pw === 0 || used + gap + pw <= room;
      setBelow((p) => (p === !fits ? p : !fits));
      setPickerH((p) => (p === ph ? p : ph));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    if (pickerRef.current) ro.observe(pickerRef.current);
    return () => { ro.disconnect(); };
  });
  return (
    <>
      <span
        ref={ref}
        data-bento-anchor={below ? "below" : "beside"}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          ...(below ? { alignSelf: "flex-start", marginBottom: pickerH + ANCHOR_GAP } : null),
        }}
      >
        {children}
        {below && (
          <span ref={pickerRef} style={{ position: "absolute", top: "100%", marginTop: ANCHOR_GAP, left: 0, zIndex: Z.stickyCell, whiteSpace: "nowrap" }}>
            {picker}
          </span>
        )}
      </span>
      {!below && (
        <span ref={pickerRef} data-bento-picker-beside="" style={{ display: "inline-flex" }}>
          {picker}
        </span>
      )}
    </>
  );
}

/** The four members the picker reads — `useBento`'s `BentoState` satisfies it (201 §B.3 added `imposed`). */
export type BentoPickerState = Readonly<{
  templates: readonly Readonly<{ code: string; label?: string | null; meta?: unknown }>[];
  templateCode: string | null;
  setTemplate: Dispatch<string>;
  imposed: boolean;
}>;

type Props = Readonly<{
  columns: readonly BentoColumnDef[];
  cards: readonly BentoCard[];
  /** The cards the viewer may move (a ReadonlySet, for 183's reason). */
  movable: ReadonlySet<BentoCardId>;
  onMove: Dispatch<BentoMove>;
  /** The empty column's line — the host's words. */
  emptyLabel: string;
}>;

/** the dragged id back from the transfer: a numeric string is a number, anything else the host's composite key */
const cardIdOf = (raw: string): BentoCardId | null => {
  if (raw === "") return null;
  return /^\d+$/.test(raw) ? +raw : raw;
};

export default function BentoBoard({ columns, cards, movable, onMove, emptyLabel }: Props) {
  const { t, lang } = useTranslation() as { t: TFn; lang: string };
  const [over, setOver] = useState<string | null>(null);
  const first = columns.length ? columns[0].code : "";
  const known = new Set(columns.map((c) => c.code));
  const byColumn = new Map<string, BentoCard[]>(columns.map((c) => [c.code, []]));
  for (const card of cards) byColumn.get(known.has(card.column) ? card.column : first)?.push(card);
  const byId = new Map(cards.map((card) => [card.id, card]));
  const drop = (itemId: BentoCardId, column: string) => {
    setOver(null);
    if (!column || !movable.has(itemId)) return;
    const card = byId.get(itemId);
    if (!card) return;
    if ((known.has(card.column) ? card.column : first) === column) return;
    onMove({ itemId, column });
  };
  if (!columns.length) {
    return (
      <div style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic", padding: "12px 2px" }}>
        {t("bento.no_columns_path", { fallback: "This Bento template has no columns yet — add some in Settings › Custom lists › Taxonomies › Bento Templates." })}
      </div>
    );
  }
  return (
    /* 190 round 8 [AM] [RULED, Laurent 2026-09-16 — "the My Bento columns
       should be a contrasting (white) colour like the Bento columns in a
       case's Bento and the case-list Bento"]: THE BOARD PAINTS ITS OWN GROUND,
       so the contrast is the board's and not the host's. MEASURED before, the
       three hosts' grounds behind a column (his light theme, --bd #ffffff ·
       --bs #f0f3f5 · --bc #f0f3f5): My Bento — the dialog's `--bs`, the same
       #f0f3f5 as the column's `--bs`, contrast none; a case's Bento — the
       `.bentoPane`'s white; the list Bento — the page's white. ONE pane colour
       against ONE named ground, chosen so they differ in EVERY theme (the
       default palettes collapse two tokens each way — light: --bs = --bc;
       dark: --bs = --bd): the ground is `--bc` (the card colour: #f0f3f5 light,
       #1a1f2b dark), the column is `--bd` (the page colour: #ffffff light —
       his "white" — #141820 dark), and the cards inside stay `--bc`, so a
       card also reads against its column in both themes. */
    <div className="board" style={{ padding: "8px 8px 12px", flex: 1, minHeight: 0, background: "var(--bc)", borderRadius: RAD.r8 }}>
      {/* 190 round 9 [AQ] [RULED, Laurent 2026-09-17]: the columns scroll
          sideways WITH ARROWS — `HScroll` (SharedUI), the 14-day strip's, the
          taxonomy table's and the two matter lists' wrapper, its scroller
          replacing the board's own bare `overflowX: auto` (a sixth column was
          cut at the right edge and nothing said so) on all three Bento
          surfaces, since they are one component. The board keeps its ground
          and its place in the host's column; the flex row of columns moves
          inside the scroller. Nothing designed — the fourth use. */}
      <HScroll>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      {columns.map((col) => {
        const inCol = byColumn.get(col.code) ?? [];
        const isOver = over === col.code;
        return (
          <div
            key={col.code}
            className="board-col"
            style={{
              /* 190 glance (i)(b) — ONE WIDTH. MEASURED on staging: 326 / 524 /
                 320 / 320 visual px under `flex: "0 0 260px"`, because a flex
                 item's automatic minimum is its min-content, and a column's
                 min-content is its widest card's unbreakable line (the cards
                 are one `nowrap` row, EisenTaskCard) — so each column was as
                 wide as its longest title. A DEFINITE `minWidth` replaces the
                 automatic one; the columns then share the board equally
                 (`flex-grow: 1`) above the floor, and a card ellipsises inside
                 its column instead of widening it. The floor is 240: four
                 columns fit the case panel (190 glance (ii)) at 1600px/xlarge,
                 where 260 overflowed it by 24px into a scrollbar. */
              /* 206 §5 — the same two figures, through the name that lets the
                 Eisenhower drawer's card cap read them instead of copying
                 them. Rendered value unchanged: `1 1 240px` / 240. */
              flex: `1 1 ${String(BENTO_COL_MIN)}px`,
              minWidth: BENTO_COL_MIN,
              minHeight: 120,
              borderRadius: RAD.r8,
              border: `1px ${isOver ? "dashed var(--ac)" : "solid var(--br)"}`,
              /* [AM] — the pane is the page colour on the board's card-colour
                 ground (above); while a card is dragged over it, the row-hover
                 token. (`var(--bg2)` stood here for the hover: no theme defines
                 it, so the column went TRANSPARENT under a drag — surfaced,
                 and two other files still read it.) */
              background: isOver ? "var(--bh)" : "var(--bd)",
              padding: BENTO_COL_PAD, // 206 §5 — the same 8, named
            }}
            onDragOver={(e) => { e.preventDefault(); if (over !== col.code) setOver(col.code); }}
            onDragLeave={() => { if (over === col.code) setOver(null); }}
            onDrop={(e) => { e.preventDefault(); const id = cardIdOf(e.dataTransfer.getData("text/plain")); if (id !== null) drop(id, col.code); else setOver(null); }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span style={{ fontSize: FS.s11, fontWeight: 700 }}>{bentoColumnLabel(col, lang)}</span>
              <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>{inCol.length}</span>
            </div>
            {/* 205 §3b [RULED — Laurent, on the correction: "the padding
                BETWEEN the cards too, so the present proportions are kept"] —
                THE BETWEEN-CARD GAP of every Bento column (task cards and
                matter cards alike stack here), 6 → 12 with `CARD_PAD`. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {inCol.map((card) => (
                <React.Fragment key={card.id}>{card.node}</React.Fragment>
              ))}
              {inCol.length === 0 ? (
                <div style={{ fontSize: FS.s10, color: "var(--tm)", fontStyle: "italic", padding: "6px 2px" }}>
                  {emptyLabel}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
      </div>
      </HScroll>
    </div>
  );
}
